import React, { FC, useCallback, useMemo } from 'react';
import fp from 'lodash/fp';
import { SxProps, Theme } from '@mui/system';

import { usePaginationFilters } from 'Modules/business/data';
import { TFunction } from 'Modules/core/i18n';

import { SearchTerm } from '../SearchTerm';


export interface FilterProps <TValue = any>{
    t: TFunction;
    initialValue?: TValue;
    path: string;
    field: string;
    onChange?: (value: TValue) => void;
    inputPropsSx?: SxProps<Theme>;
    textFieldPropsSx?: SxProps<Theme>;
    isGray?: boolean;
    fullWidth?: boolean;
    minSearchCharCount?: number;
    ruleRegExp?: RegExp;
}


const VALUE_REG_EXP = new RegExp(/[^а-яА-Я\w ]/, 'g');
const DEFAULT_MIN_CHAR_COUNT = 3;


export const FilterSearchTerm: FC<FilterProps<string>> = ({
    path, t, initialValue, onChange, field, textFieldPropsSx, inputPropsSx, isGray = true,
    fullWidth = true, minSearchCharCount = DEFAULT_MIN_CHAR_COUNT, ruleRegExp = VALUE_REG_EXP,
}) => {
    const { setFilters } = usePaginationFilters<string>(path, field);

    const setFiltersDebounced = useMemo(
        () => fp.debounce(1000, setFilters),
        [setFilters],
    );

    const onChangeSearch = useCallback((value: string) => {
        const formattedValue = value.replace(ruleRegExp, '');

        if (formattedValue.length >= minSearchCharCount) {
            const filterValue = formattedValue.toLocaleLowerCase();

            if (onChange)
                onChange(filterValue);
            setFiltersDebounced(filterValue);
        } else {
            if (onChange)
                onChange('');
            setFiltersDebounced('');
        }
    }, [setFiltersDebounced, onChange, minSearchCharCount, ruleRegExp]);


    return (
        <SearchTerm
            fullWidth={fullWidth}
            isGray={isGray}
            placeholder={t('filterPlaceholder') as string}
            initialValue={initialValue}
            onChange={onChangeSearch}
            inputPropsSx={inputPropsSx}
            textFieldPropsSx={textFieldPropsSx}
        />
    );
};
